import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Spinner } from '@1trading/react';
import { Play } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dac8YearOptions } from '@/features/dac8/dac8-year-options';
import { ReportFilesTable } from '@/features/reports/report-files-table';
import { useRequestDac8AuditReport } from '@/hooks/queries/compliance/use-dac8-audit';
import { useReports } from '@/hooks/queries/reports/use-reports';
import { type ReportReadyPush, useAsyncReport } from '@/hooks/use-async-report';
import { useQueryErrorToast } from '@/hooks/use-query-error-toast';
import { getErrorMessage, isForbiddenError } from '@/lib/api/error';
import { DAC8_REPORT_TYPE } from '@/services/api/dac8-audit';
import type { ReportsQuery } from '@/services/api/reports';
import { daysAgo, toDateInputValue } from '@/utils/date';

// A handful of runs a year, so the default window is wider than the daily reports
const DEFAULT_RANGE_DAYS = 90;
const FETCH_ERROR_TOAST_ID = 'dac8-audit-fetch-failed';
const VIEW_PATHNAME = '/compliance/dac8/audit/view';
const YEAR_OPTIONS = dac8YearOptions();

const defaultQuery = (): ReportsQuery => ({
  type: DAC8_REPORT_TYPE,
  from: toDateInputValue(daysAgo(DEFAULT_RANGE_DAYS)),
  to: toDateInputValue(new Date()),
});

/** The DAC8 audit section: generate a run for a reporting year and browse past runs. */
export const Dac8Audit = () => {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<ReportsQuery>(defaultQuery);
  // Auto-fetches once on mount with the defaults; afterwards only the Search
  // button moves `submitted`, so edits to the controls never fetch on their own.
  const [submitted, setSubmitted] = useState<ReportsQuery>(draft);
  const [year, setYear] = useState<number>(YEAR_OPTIONS[0] ?? new Date().getUTCFullYear());
  const openReport = useCallback((message: ReportReadyPush) => navigate({ pathname: VIEW_PATHNAME, search: `?key=${encodeURIComponent(message.key)}` }), [navigate]);

  const reportsQuery = useReports(submitted);
  const requestMutation = useRequestDac8AuditReport();
  const { isGenerating, requestReport } = useAsyncReport({
    reportType: DAC8_REPORT_TYPE,
    label: 'DAC8 audit report',
    request: () => requestMutation.mutateAsync({ year }),
    onReady: openReport,
  });
  const { data, isPending, isFetching, isError, error, errorUpdatedAt, refetch } = reportsQuery;
  // The API limits this report to the DAC8 reviewer group; a 403 is a state to explain, not a fault to toast
  const isForbidden = isError && isForbiddenError(error);

  useQueryErrorToast({ isError: isError && !isForbidden, errorUpdatedAt }, 'Failed to fetch DAC8 audit reports', FETCH_ERROR_TOAST_ID);

  const handleSearch = () => {
    if (draft.from === submitted.from && draft.to === submitted.to) {
      refetch();
      return;
    }
    setSubmitted(draft);
  };

  if (isForbidden) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>DAC8 Audit</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">This report contains customer identity data and is limited to the DAC8 reviewer group. Ask Compliance if you need access.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <CardTitle>DAC8 Audit</CardTitle>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="dac8-audit-year">Reporting Year</Label>
            <Select onValueChange={(value) => setYear(Number(value))} value={String(year)}>
              <SelectTrigger className="w-32" id="dac8-audit-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {YEAR_OPTIONS.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button className="gap-2" disabled={isGenerating} onClick={() => void requestReport()} type="button">
            {isGenerating ? <Spinner className="size-4" /> : <Play className="size-4" />}
            {isGenerating ? 'Generating' : 'Generate'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="dac8-audit-from">From</Label>
            <Input className="w-44" id="dac8-audit-from" onChange={(event) => setDraft((previous) => ({ ...previous, from: event.target.value }))} type="date" value={draft.from} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="dac8-audit-to">To</Label>
            <Input className="w-44" id="dac8-audit-to" onChange={(event) => setDraft((previous) => ({ ...previous, to: event.target.value }))} type="date" value={draft.to} />
          </div>
          <Button className="gap-2" disabled={isFetching} onClick={handleSearch} type="button">
            {isFetching ? <Spinner className="size-4" /> : null}
            Search
          </Button>
        </div>
        {isError ? (
          <div className="flex items-center gap-3">
            <p className="text-sm text-red-500">Failed to fetch DAC8 audit reports: {getErrorMessage(error)}</p>
            <Button onClick={() => refetch()} size="sm" type="button" variant="outline">
              Retry
            </Button>
          </div>
        ) : null}
        <ReportFilesTable
          emptyMessage="No DAC8 audit reports found in the selected date range. Generate one for a reporting year, or widen the range."
          files={data?.files}
          isPending={isPending}
          viewPathname={VIEW_PATHNAME}
        />
      </CardContent>
    </Card>
  );
};
Dac8Audit.displayName = 'Dac8Audit';
